import { Award, Droplets, MapPin, Target, TrendingUp, Users } from 'lucide-react';
import { useTranslation } from '@/i18n/hooks';
import { useInView } from '@/utils';
import { MetricCard } from './metricCard';

export function Metrics() {
  const { t } = useTranslation('home');

  const [sectionRef, isSectionInView] = useInView({ threshold: 0.1 });
  const [headerRef, isHeaderInView] = useInView({ threshold: 0.2 });

  const metrics = [
    {
      translationKey: 'metrics.hectares' as const,
      icon: MapPin,
      value: 50000,
      suffix: t('metrics.suffix.hectares'),
      prefix: undefined,
      decimals: 0,
    },
    {
      translationKey: 'metrics.clients' as const,
      icon: Users,
      value: 250,
      suffix: t('metrics.suffix.clients'),
      prefix: undefined,
      decimals: 0,
    },
    {
      translationKey: 'metrics.experience' as const,
      icon: Award,
      value: 5,
      suffix: t('metrics.suffix.years'),
      prefix: undefined,
      decimals: 0,
    },
    {
      translationKey: 'metrics.drones' as const,
      icon: Target,
      value: 15,
      suffix: t('metrics.suffix.drones'),
      prefix: undefined,
      decimals: 0,
    },
    {
      translationKey: 'metrics.economy' as const,
      icon: TrendingUp,
      value: 30,
      suffix: t('metrics.suffix.economy'),
      prefix: undefined,
      decimals: 0,
    },
    {
      translationKey: 'metrics.precision' as const,
      icon: Droplets,
      value: 95,
      suffix: t('metrics.suffix.precision'),
      prefix: undefined,
      decimals: 0,
    },
  ];

  return (
    <section
      ref={sectionRef as React.RefObject<HTMLElement>}
      id="metrics"
      className="relative overflow-hidden bg-slate-50 py-16 sm:py-20 lg:py-32 dark:bg-slate-900"
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-30 dark:opacity-20">
        <div className="absolute inset-0 bg-[linear-gradient(45deg,#1e293b08_1px,transparent_1px),linear-gradient(-45deg,#64748b08_1px,transparent_1px)] bg-size-[40px_40px]"></div>
      </div>
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-16 right-0 h-64 w-64 rounded-full bg-primary/10 blur-3xl animate-pulse sm:top-32 sm:right-32"></div>
        <div className="absolute -bottom-16 left-0 h-80 w-80 rounded-full bg-primary/5 blur-3xl animate-pulse delay-1000 sm:bottom-32 sm:left-32"></div>
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className="mb-12 text-center lg:mb-16"
        >
          <h2
            className={`mb-4 text-3xl font-bold text-slate-900 sm:text-4xl lg:text-5xl dark:text-slate-100 slide-in-left-ready ${isHeaderInView ? 'in-view' : ''}`}
          >
            {t('metrics.title')}
          </h2>
          <p
            className={`mx-auto max-w-2xl text-lg text-slate-600 dark:text-slate-400 fade-in-ready ${isHeaderInView ? 'in-view' : ''}`}
            style={{ transitionDelay: '200ms' }}
          >
            {t('metrics.subtitle')}
          </p>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 gap-6 sm:gap-8 lg:grid-cols-3">
          {metrics.map((metric, index) => (
            <MetricCard
              key={metric.translationKey}
              translationKey={metric.translationKey}
              icon={metric.icon}
              value={metric.value}
              suffix={metric.suffix}
              prefix={metric.prefix}
              decimals={metric.decimals}
              isInView={isSectionInView}
              delay={300 + index * 100}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
